import * as XLSX from 'xlsx';
import { ENERGIES, USAGES, isValidDjclim, isValidDju, isValidRatio, setUsageRatio, usageRatio } from './domain';
import { parseClipboard, parseNum } from './sheet';
import type { DegreeDays, RatioTable } from './types';

export interface ImportResult {
  degrees: DegreeDays;
  ratios: RatioTable;
  /** Nombre de valeurs de ratios reprises. */
  applied: number;
  /** Lignes non reconnues (typologie inconnue, valeur hors plage…). */
  skipped: string[];
  djuFound: boolean;
  djclimFound: boolean;
}

const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

const USAGE_BY_LABEL = new Map(USAGES.map((u) => [norm(u.label), u.key]));

/**
 * Lit un export tableur de ratios (CSV ou TSV).
 * Lignes reconnues : `DJU;2500`, `DJClim;110`, puis `Typologie;Chauffage;ECS;Froid`.
 */
export function parseRatioFile(text: string, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  let degrees = { ...base.degrees };
  let ratios = base.ratios;
  let applied = 0;
  let djuFound = false;
  let djclimFound = false;
  const skipped: string[] = [];

  for (const cells of parseClipboard(text)) {
    const head = norm(cells[0] ?? '');
    if (head === '' && cells.length <= 1) continue;

    if (head === 'dju' || head === 'djuchauffage' || head === 'mondju') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDju(n)) { degrees = { ...degrees, dju: n }; djuFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }
    if (head === 'djclim' || head === 'djclimfroid' || head === 'mondjclim') {
      const n = parseNum(cells[1] ?? '');
      if (isValidDjclim(n)) { degrees = { ...degrees, djclim: n }; djclimFound = true; } else skipped.push(cells.join(' ; '));
      continue;
    }

    const usage = USAGE_BY_LABEL.get(head);
    if (!usage) {
      if (head !== 'typologie' && head !== '') skipped.push(cells.join(' ; '));
      continue;
    }
    ENERGIES.forEach((e, i) => {
      const raw = cells[i + 1];
      if (raw === undefined || raw.trim() === '') return;
      const n = parseNum(raw);
      if (!isValidRatio(n)) { skipped.push(`${cells[0]} — ${e.label} : ${raw}`); return; }
      if (n !== usageRatio(ratios, usage)[e.key]) ratios = setUsageRatio(ratios, usage, e.key, n);
      applied += 1;
    });
  }

  return { degrees, ratios, applied, skipped, djuFound, djclimFound };
}

export function parseRatioWorkbook(data: ArrayBuffer, base: { degrees: DegreeDays; ratios: RatioTable }): ImportResult {
  const workbook = XLSX.read(data, { type: 'array' });
  const sheet = workbook.Sheets.Ratios ?? workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('Le classeur ne contient aucune feuille de ratios.');
  return parseRatioFile(XLSX.utils.sheet_to_csv(sheet, { FS: ';', blankrows: false }), base);
}

/** Numeric Excel cells preserve the current values without a CSV conversion step. */
export function ratioTemplate(degrees: DegreeDays, ratios: RatioTable): XLSX.WorkBook {
  const rows = [
    ['DJU', degrees.dju],
    ['DJClim', degrees.djclim],
    [],
    ['Typologie', ...ENERGIES.map((e) => e.label)],
    ...USAGES.map((u) => {
      const r = usageRatio(ratios, u.key);
      return [u.label, ...ENERGIES.map((e) => r[e.key])];
    }),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 24 }, { wch: 16 }];
  sheet['!autofilter'] = { ref: `A4:D${rows.length}` };
  for (let row = 4; row < rows.length; row += 1) {
    for (let col = 1; col <= ENERGIES.length; col += 1) {
      sheet[XLSX.utils.encode_cell({ r: row, c: col })].z = '0.0';
    }
  }
  const instructions = XLSX.utils.aoa_to_sheet([
    ['Modèle de ratios Plan — mode d’emploi'],
    ['La feuille Ratios est pré-remplie avec vos valeurs courantes.'],
    ['Modifiez uniquement les valeurs numériques ; conservez les libellés et les colonnes.'],
    ['DJU : de 500 à 5 000. DJClim : de 10 à 1 000.'],
    ['Ratios : de 1 à 1 000 kWh/m²/an, pour la période de référence (< 1948).'],
    ['Les coefficients de performance existants sont conservés ; ce modèle ne les modifie pas.'],
    ['Enregistrez le fichier .xlsx, puis utilisez « Importer un fichier de ratios ».'],
    ['Les valeurs hors plage sont signalées et ignorées, jamais remplacées par une valeur par défaut.'],
    ['L’import alimente Mes ratios ; cliquez ensuite « Appliquer mes ratios à mon projet ».'],
    ['Le prototype ne calcule pas les formules Excel : saisissez des valeurs numériques.'],
  ]);
  instructions['!cols'] = [{ wch: 115 }];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Ratios');
  XLSX.utils.book_append_sheet(workbook, instructions, 'Mode d’emploi');
  return workbook;
}

export function downloadRatioTemplate(degrees: DegreeDays, ratios: RatioTable): void {
  XLSX.writeFile(ratioTemplate(degrees, ratios), 'mes-ratios.xlsx', { compression: true });
}
